import { ChevronDownIcon, ShieldExclamationIcon } from "@heroicons/react/24/solid";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useCurrentUser } from "@/hooks/use-current-user";

import { useClaim } from "../../api/use-claim";
import { useDiscardCircumstance } from "../../api/use-claim-insights";
import { CircumstanceCard } from "./circumstance-card";
import type { ExtraordinaryCircumstance } from "./circumstance-config";

function safeParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/**
 * "Cause éxonératoire" row — sits among ClaimDetails' other <dt>/<dd> rows. The value is a badge;
 * clicking it unfolds the same CircumstanceCard as the priority display above — full (not preview),
 * full width, below the row.
 */
export function ClaimReasonCxDisclosure({ claimId }: { claimId: string }) {
  const { can } = useCurrentUser();
  const { data: claim } = useClaim(claimId);
  const discardCircumstance = useDiscardCircumstance(claimId);

  const insight = claim?.insights?.find((candidate) => candidate?.type?.type === "CX");
  const rawMetadata = insight?.metadata;
  const parsed = typeof rawMetadata === "string" ? safeParse(rawMetadata) : rawMetadata;
  const detected: ExtraordinaryCircumstance[] = Array.isArray(parsed)
    ? (parsed as ExtraordinaryCircumstance[])
    : [];

  const active = detected.filter(
    (cx) =>
      cx.eventDetected && !cx.discarded && ["high", "medium"].includes((cx.confidence ?? "").toLowerCase()),
  );
  const top = active[0];

  if (!can([...UPCLAIM_ROLES, "LAWYER"])) {
    return null;
  }

  return (
    <Collapsible className="py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
      <dt className="text-sm leading-6 font-medium text-gray-700">
        <span className="inline-flex">
          <ShieldExclamationIcon className="mr-2 h-5 w-5 text-slate-400" />
          Cause éxonératoire
        </span>
      </dt>
      <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
        {top ? (
          <CollapsibleTrigger className="group inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20 hover:bg-red-100">
            Circonstance extraordinaire possible détectée
            <ChevronDownIcon className="h-3.5 w-3.5 flex-none transition-transform group-data-[panel-open]:rotate-180" />
          </CollapsibleTrigger>
        ) : (
          "Aucune détectée"
        )}
      </dd>
      {top ? (
        <CollapsibleContent className="sm:col-span-3">
          <div className="mt-3">
            <CircumstanceCard
              circumstance={top}
              busy={discardCircumstance.isPending}
              onDiscard={() =>
                discardCircumstance.mutate({
                  cxType: top.cxType,
                  iata: top.iata ?? "",
                  date: top.date ?? "",
                  discarded: true,
                })
              }
            />
          </div>
        </CollapsibleContent>
      ) : null}
    </Collapsible>
  );
}
